/**
 * Vertrag des Kontobereichs (Stufe H, `apps/app/docs/STUFE_H_PLAN.md` §2). H.1 liest die Kontoangaben aus `/status`
 * und `/me`; neu ist nur die Mitgliederliste (H.1b). Sie nennt keine E-Mail, keine Cognito-`sub` und keine Namen:
 * je Mitglied nur Rolle, Beginn der Mitgliedschaft und ob es der angemeldete Benutzer ist („Sie“).
 */
import { z } from 'zod';
import { AccountProfile, Invoice, IsoDateTime, MemberRole, Version } from '@pat/core/entities';

/** Legacy-Kundennummer „P“ + `user_id`; `tenantId` ist die Legacy-`user_id` (Plan §1 H.1). */
export function customerNumber(tenantId: string): string {
  return `P${tenantId}`;
}

export const MemberView = z.object({
  role: MemberRole,
  /** Beginn der Mitgliedschaft (`Member.createdAt`). */
  createdAt: IsoDateTime,
  /** Der angemeldete Benutzer selbst. */
  self: z.boolean(),
});
export type MemberView = z.infer<typeof MemberView>;

/** Mitglieder des Mandanten: „Sie“ zuerst, danach nach Beginn der Mitgliedschaft (älteste zuerst). */
export const MembersResponse = z.object({ members: z.array(MemberView) });
export type MembersResponse = z.infer<typeof MembersResponse>;

// ---------------------------------------------------------------------------
// H.2 (Entscheidungen Claude/Flori 25.10.2026): Kundendaten lesend, nur der Kontaktteil ist änderbar; Rechnungen mit
// Status und Beträgen samt „Stand“; beides nur für `owner`. Schreiben von Rechnungen nur Import und Nachlauf.

/** Kundendaten wie core `AccountProfile`, ohne Mandant; `version` für den Vergleich beim Speichern. */
export const AccountProfileView = AccountProfile.omit({ tenantId: true, updatedAt: true });
export type AccountProfileView = z.infer<typeof AccountProfileView>;

/** `profile` fehlt, solange für das Konto noch keine Stammdaten importiert sind. */
export const AccountResponse = z.object({ profile: AccountProfileView.optional() });
export type AccountResponse = z.infer<typeof AccountResponse>;

/**
 * Kontaktteil ändern (core `appEditableProfileFields`). Leerer Text löscht ein Feld; `expectedVersion` wie bei den
 * übrigen versionierten Speichern, Konflikt → 409.
 */
export const AccountContactUpdate = z
  .object({
    expectedVersion: Version,
    phone: z.string().trim().max(40),
    contactEmail: z.union([z.literal(''), z.string().trim().email().max(254)]),
    ccEmail: z.union([z.literal(''), z.string().trim().email().max(254)]),
    tokenExpiryReminder: z.boolean(),
  })
  .strict();
export type AccountContactUpdate = z.infer<typeof AccountContactUpdate>;

/** Rechnungsnummer wie im Altsystem (`P<yy>-<n>`); Schlüssel und Pfadparameter. */
export const INVOICE_NO = /^P\d{2}-[1-9]\d{0,9}$/;

/** Listeneintrag ohne PDF-Schlüssel und ohne Altstatus; `hasPdf` steuert den Download. */
export const InvoiceListItem = Invoice.pick({
  invoiceNo: true,
  issuedAt: true,
  periodFrom: true,
  periodTo: true,
  net: true,
  vat: true,
  gross: true,
  vatRate: true,
  payType: true,
  status: true,
  paidAt: true,
  cancelledAt: true,
}).extend({ hasPdf: z.boolean() });
export type InvoiceListItem = z.infer<typeof InvoiceListItem>;

export const INVOICE_PAGE_DEFAULT = 24;
export const INVOICE_PAGE_MAX = 48;
export const InvoicesQuery = z
  .object({
    cursor: z.string().min(1).max(2048).optional(),
    limit: z.coerce.number().int().min(1).max(INVOICE_PAGE_MAX).default(INVOICE_PAGE_DEFAULT),
  })
  .strict();
export type InvoicesQuery = z.infer<typeof InvoicesQuery>;

/** Neueste zuerst; `asOf` = ältester Stand der gezeigten Zeilen (Altsystem, Nachlauf). */
export const InvoicesResponse = z.object({ invoices: z.array(InvoiceListItem), nextCursor: z.string().max(2048).optional(), asOf: IsoDateTime.optional() });
export type InvoicesResponse = z.infer<typeof InvoicesResponse>;

/** Kurzlebige signierte Adresse zum PDF (60–300 s); Antwort mit `Cache-Control: no-store`. */
export const InvoiceUrlResponse = z.object({ url: z.string().url(), expiresAt: IsoDateTime });
export type InvoiceUrlResponse = z.infer<typeof InvoiceUrlResponse>;

/**
 * Offene Rechnungen für den Hinweis auf der Startseite wie im Altsystem: `pat_get_users_open_invoices($user_id, 7)`
 * (lib-config `lib/pat_get_users_open_invoices.php:5-17`, Startseite A `include/module/app/action/index.php:6`,
 * `template/index.tpl.php:31-34`) zählt `user_bill_status < 10` mit Rechnungsdatum bis heute minus 7 Tage. In core:
 * offen, Erinnerung, Mahnung (Altstatus 1, 2, 3, 5, 9); bezahlt, Lastschrift in Bearbeitung (Altstatus 10) und
 * storniert zählen nicht.
 */
export const OPEN_INVOICE_STATUSES: readonly Invoice['status'][] = ['open', 'reminder', 'dunning'];
export const OPEN_INVOICE_MIN_AGE_DAYS = 7;
const berlinDay = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' });

/** Letzter mitgezählter Rechnungstag: Kalendertag in Europe/Berlin von jetzt minus 7 × 24 h (Legacy `date('Y-m-d', mktime() - 7*24*60*60)`). */
export function openInvoiceCutoff(now: Date): string {
  return berlinDay.format(new Date(now.getTime() - OPEN_INVOICE_MIN_AGE_DAYS * 86_400_000));
}

export function isOpenInvoice(invoice: Pick<Invoice, 'status' | 'issuedAt'>, cutoff: string): boolean {
  return OPEN_INVOICE_STATUSES.includes(invoice.status) && invoice.issuedAt <= cutoff;
}

/** Anzahl offener Rechnungen (nur Inhaber), `no-store`. */
export const OpenInvoicesResponse = z.object({ open: z.number().int().nonnegative() }).strict();
export type OpenInvoicesResponse = z.infer<typeof OpenInvoicesResponse>;
